import "./style.scss";

import { ListItemNode, ListNode } from "@lexical/list";
import { MarkNode } from "@lexical/mark";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HeadingNode } from "@lexical/rich-text";
import type { TTitleBeneficiaries } from "components/AgreementValidationModal/utils";
import type { UserVariables } from "components/shared/AgreementModal";
import { PortalType } from "contexts/portal.context";
import { isObject } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { SetStateAction } from "react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgreementTemplate } from "store/agreementsStore";
import { setSavedEditorContent } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";
import { EPortalType } from "store/types";

import InsertVariable from "../ContractEditor/InsertVariable";
import ArticlePlugin from "./ArticlePlugin";
import { ArticleContainerNode } from "./ArticlePlugin/ArticleContainerNode";
import { ArticleContentNode } from "./ArticlePlugin/ArticleContentNode";
import { ArticleTitleNode } from "./ArticlePlugin/ArticleTitleNode";
import { channelVariablePlaceholder } from "./channelVariable";
import ChooseChannelModal from "./ChooseChannelModal";
import CommentPlugin from "./CommentPlugin";
import { ConditionalNode } from "./ConditionalNode";
import ConditionalPlugin from "./ConditionalPlugin";
import { ConditionalContainerNode } from "./ConditionalPlugin/ConditionalContainerNode";
import { ConditionalContentNode } from "./ConditionalPlugin/ConditionalContentNode";
import { ConditionalTitleNode } from "./ConditionalPlugin/ConditionalTitleNode";
import FloatingTextFormatToolbarPlugin from "./FloatingTextFormatPlugin";
import { FilledVariableNode } from "./LexicalFilledVariableNode";
import { VariableNode } from "./LexicalVariableNode";
import DraggableBlockPlugin from "./MakeOptional/MakeOptional";
import { ImageNode } from "./nodes/ImageNode";
import { PageBreakNode } from "./nodes/PageBreakNode";
import { SignatureBlockNode } from "./nodes/SignatureBlockNode";
import theme from "./PlaygroundEditorTheme";
import {
    AutoFocusPlugin,
    EditorSavePlugin,
    FillVariablesPlugin,
    INSERT_TEXT_COMMAND,
    InsertTextPlugin,
    OnChangePlugin,
    ReferencePlugin,
    SetDraftDataPlugin,
    VariablePlugin,
} from "./plugins";
import ClipboardHandlerPlugin from "./plugins/ClipboardHandlerPlugin";
import DefaultContentPlugin from "./plugins/DefaultContentPlugin";
import ImagesPlugin from "./plugins/ImagesPlugin";
import PageBreakPlugin from "./plugins/PageBreakPlugin";
import { ReferenceNode } from "./ReferenceNode";
import RepeatablePlugin from "./RepeatablePlugin";
import { RepeatableContainerNode } from "./RepeatablePlugin/RepeatableContainerNode";
import { RepeatableContentNode } from "./RepeatablePlugin/RepeatableContentNode";
import { RepeatableTitleNode } from "./RepeatablePlugin/RepeatableTitleNode";
import type { RepeatableArticle } from "./RepeatablePlugin/repeatBy";
import { SignatureBlockPlugin } from "./SignatureBlockPlugin";
import ToolbarPlugin from "./ToolbarPlugin/ToolbarPlugin";
import { useAgreementVariables } from "./variables";

export type TBeneficiariesFormData = {
    beneficiaryIds: number[] | undefined;
    titleBeneficiaries: TTitleBeneficiaries | undefined;
};

export type TArticlesFormData = {
    articles: RepeatableArticle[];
};

interface ContractEditorLexicalProps {
    agreement: AgreementTemplate;
    /**
     * Variables state (change variables state)
     */
    userVariables?: UserVariables;
    /**
     * Agreement known variables
     */
    agreementUserVariables?: Record<string, any> | undefined;
    /**
     * Component called from agremeent modal
     */
    isAgreementSetup?: boolean;
    /**
     * Component called from agreement validation page
     */
    isAgreementEdit?: boolean;
    /**
     * Component called from agreement settings
     */
    isAgreementSettings?: boolean;
    isReadOnly?: boolean;
    onSetupSave?: (content: { stringifiedBlocks: string; html: string }) => void;
    /**
     * Active react blocker, catch editor changes
     */
    setIsDirty?: React.Dispatch<SetStateAction<boolean>>;
    /**
     * Props used for Multi-signature, form state data for beneficiaries associated to title
     */
    beneficiariesFormData?: TBeneficiariesFormData;
    /**
     * Articles of the work. The setup editor repeats an article block once per title.
     */
    articlesFormData?: TArticlesFormData;
    /**
     * Check if contract uses multi-signature
     */
    hasMultiSignature?: boolean;
    /**
     * If edit template drawer is open
     */
    isDrawerOpen?: boolean;
}

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error) {
    console.error(error);
}

export const initialEditorState =
    '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

const ContractEditorLexical = (props: ContractEditorLexicalProps) => {
    const {
        agreement,
        userVariables,
        agreementUserVariables,
        isAgreementSetup = false,
        isAgreementEdit = false,
        isAgreementSettings = false,
        isReadOnly = false,
        onSetupSave,
        setIsDirty,
        beneficiariesFormData,
        articlesFormData,
        hasMultiSignature,
        isDrawerOpen,
    } = props;

    const resolvedTemplateId: number | undefined = isAgreementSettings ? agreement.id : (agreement as any)?.templateId;
    const initialConfig = {
        namespace: "AgreementEditor",
        theme,
        onError,
        editable: !isReadOnly,
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            VariableNode,
            FilledVariableNode,
            ArticleContainerNode,
            ArticleContentNode,
            ArticleTitleNode,
            ReferenceNode,
            ConditionalContainerNode,
            ConditionalContentNode,
            ConditionalTitleNode,
            ConditionalNode,
            MarkNode,
            RepeatableContainerNode,
            RepeatableContentNode,
            RepeatableTitleNode,
            PageBreakNode,
            SignatureBlockNode,
            ImageNode,
        ],
    };

    const portalType = useContext(PortalType);
    const isLexical = isObject(agreement.blocks) && "root" in agreement.blocks;
    /**
     * For an unknown reason
     * Lexical works with `blocks` as a string
     * when it loads up
     */
    const editorSavedState = JSON.stringify(agreement.blocks);

    const [t] = useTranslation();
    const { companyInfos, getCompanySettings } = useCompanySettings();
    const usesChannelCompensation = getCompanySettings("uses_interchannel_compensation");
    const dispatch = useAppDispatch();
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const formatTypes = useAppSelector((state) => state.titleFormatTypes.items);
    const titleFormatTypesStatus = useAppSelector((state) => state.titleFormatTypes.status);
    const hasDocoonIntegration = useAppSelector((state) => state.integrations.hasDocoonIntegration);
    const [showChannelSelectionModal, setShowChannelSelectionModal] = useState(false);
    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);

    // Template context uses .type; agreement instance uses .relatedResource.
    const isFrameworkContract =
        (agreement as { type?: string })?.type === "framework_contract" ||
        (agreement as { relatedResource?: string })?.relatedResource === "frameworkContract";

    const { variables: allVariables } = useAgreementVariables(portalType, { isFrameworkContract });

    useEffect(() => {
        if (portalType !== EPortalType.BOOK || titleFormatTypesStatus !== "idle") return;
        dispatch(fetchTitleFormatTypes());
    }, [dispatch, portalType, titleFormatTypesStatus]);

    const isNewAndEmptyTemplate =
        agreement?.blocks?.root?.children?.length === 1 && !agreement?.blocks?.root?.children[0]?.children?.length;

    const onRef = (_floatingAnchorElem: HTMLDivElement) => {
        if (_floatingAnchorElem !== null) {
            setFloatingAnchorElem(_floatingAnchorElem);
        }
    };

    function onChange(savedEditorData: { stringifiedBlocks: string; html: string }) {
        if (onSetupSave) onSetupSave(savedEditorData);
    }

    const onEditorSave = (savedEditorData: { stringifiedBlocks: string; html: string }) => {
        dispatch(setSavedEditorContent(savedEditorData));
    };

    // this plugin needs to stay here to make it easier to interact with rest of components code
    // but it could be moved to the plugins file
    const InsertVariablePlugin = () => {
        const [editor] = useLexicalComposerContext();

        const handleSelectVariable = (placeholder: string) => {
            setIsDirty && setIsDirty!(true);
            if (placeholder === "{{channel:XX}}") setShowChannelSelectionModal(true);
            else insertVariable(placeholder);
        };

        const insertVariable = (placeholder: string) => {
            editor.dispatchCommand(INSERT_TEXT_COMMAND, placeholder + " ");
        };

        /**
         * Action when validate chosen channel
         * @param channelId can be channel id or group channel id
         * @param hasSelectedGroup check if the selection is a group of channels
         */
        const handleSelectChannel = (channelId: number, hasSelectedGroup: boolean, formatTypeId: number | null) => {
            const kind = hasSelectedGroup ? "channelGroup" : "channel";
            insertVariable(channelVariablePlaceholder(kind, channelId, formatTypeId));
            setShowChannelSelectionModal(false);
        };

        return (
            <div className="insert-variable-component">
                <ChooseChannelModal
                    open={showChannelSelectionModal}
                    onClose={() => setShowChannelSelectionModal(false)}
                    usesChannelCompensation={usesChannelCompensation}
                    channels={channels}
                    handleSelectChannel={handleSelectChannel}
                    channelCompensationGroups={isFrameworkContract ? [] : channelCompensationGroups}
                    formatTypes={formatTypes}
                />
                <InsertVariable variables={allVariables ?? {}} onSelect={handleSelectVariable} />
            </div>
        );
    };

    const handleDirty = () => {
        if (isDrawerOpen || isAgreementEdit) {
            setIsDirty && setIsDirty(true);
        }
    };

    return (
        <div className="contract-editor-element" style={isAgreementEdit ? { padding: "0px", border: "none" } : {}}>
            <LexicalComposer
                initialConfig={{
                    ...initialConfig,
                    editorState: isLexical ? editorSavedState : initialEditorState,
                }}
            >
                <div className={`editor ${isAgreementSetup ? "compact" : ""}`}>
                    {!isLexical && <SetDraftDataPlugin agreement={agreement} />}
                    <ToolbarPlugin
                        isAgreementSetup={isAgreementSetup}
                        isAgreementEdit={isAgreementEdit}
                        isAgreementSettings={isAgreementSettings}
                        InsertVariablePlugin={InsertVariablePlugin}
                    />
                    <ClipboardHandlerPlugin />
                    <ImagesPlugin />
                    <EditorSavePlugin onEditorSave={onEditorSave} />
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <PageBreakPlugin />
                    <OnChangePlugin onChange={onChange} handleDirtyEditor={handleDirty} />
                    <DefaultContentPlugin showDefaultContent={isNewAndEmptyTemplate} />
                    <SignatureBlockPlugin
                        companyName={companyInfos?.name ?? ""}
                        isAgreementSettings={isAgreementSettings}
                        beneficiariesFormData={beneficiariesFormData}
                        isAgreementEdit={isAgreementEdit}
                        isAgreementSetup={isAgreementSetup}
                        templateId={resolvedTemplateId}
                        hasDocoonIntegration={hasDocoonIntegration}
                    />
                    <ListPlugin />
                    <InsertTextPlugin />
                    {!isAgreementEdit && <VariablePlugin allVariables={allVariables} />}
                    <TabIndentationPlugin />
                    <ArticlePlugin />
                    <ConditionalPlugin />
                    <RepeatablePlugin
                        beneficiariesFormData={isAgreementSetup ? beneficiariesFormData : undefined}
                        articlesFormData={isAgreementSetup ? articlesFormData : undefined}
                    />
                    <FloatingTextFormatToolbarPlugin isAgreementEdit={isAgreementEdit} />
                    <ReferencePlugin />
                    {!isAgreementSetup && !isAgreementEdit && floatingAnchorElem && (
                        <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
                    )}
                    {isAgreementEdit && <CommentPlugin providerFactory={undefined} />}
                    {userVariables && (
                        <FillVariablesPlugin
                            userVariables={userVariables}
                            agreementUserVariables={agreementUserVariables}
                            hasMultiSignature={hasMultiSignature as boolean}
                        />
                    )}
                    <RichTextPlugin
                        contentEditable={
                            <div
                                ref={onRef}
                                className={isAgreementEdit ? "ContentEditable-wrapper__agreementEdit" : ""}
                            >
                                <ContentEditable
                                    className={`ContentEditable__root${
                                        isAgreementSetup ? " ContentEditable__noOverflow" : ""
                                    }${isAgreementEdit ? " ContentEditable__agreementEdit" : ""}`}
                                />
                            </div>
                        }
                        placeholder={
                            !isAgreementSetup && !isAgreementEdit ? (
                                <div className="Placeholder__root">{t("RichEditor.StartWriting")}</div>
                            ) : (
                                <></>
                            )
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </div>
            </LexicalComposer>
        </div>
    );
};

export default ContractEditorLexical;
